import { useState, useEffect, useMemo, useRef } from "react"
import type { UserFromClient, UserFromDb } from "shared/src/DatabaseObjects/DatabaseObjects"
import { DEFAULT_AVAILABLE_USERS } from "shared/src/constants/constants"
import { useAppContext, useConfigContext } from "shared/src/libs/contextLib/contextLib"
import { Button } from "shared/src/components/shadcn/components/atoms/buttons"
import { Input } from "shared/src/components/shadcn/components/ui/input"
import { toast } from "sonner"
import { Switch } from "shared/src/components/shadcn/components/ui/switch"
import { useFormFields, useMountEffect } from "shared/src/libs/hooksLib/hooksLib"
import { getHumanReadableAge } from "shared/src/libs/dateLib/dateLib"
import { validateEmail } from "shared/src/libs/validationLib/validationLib"
import { patchUser, createUser, deleteUser, getUsers } from "shared/src/libs/apiLib/apiLib"
import { capitalizeString } from "shared/src/libs/stringLib/stringLib"
import { getUserRole } from "./getUserRole"
import { clientApi } from "shared/src/libs/apiLib/apiLib"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "shared/src/components/shadcn/components/ui/card"
import { Field, FieldLabel, FieldContent } from "shared/src/components/shadcn/components/ui/field"
import { Alert, AlertDescription } from "shared/src/components/shadcn/components/ui/alert"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "shared/src/components/shadcn/components/ui/table"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "shared/src/components/shadcn/components/ui/select"
import { IconButton } from "shared/src/components/shadcn/components/atoms/buttons"
import { LimitBadge } from "shared/src/components/shadcn/components/atoms/badges"
import { useConfirmationDialog } from "shared/src/components/shadcn/components/molecules/dialogs/ConfirmationDialog"
import { Info, Trash2, CircleAlert } from "lucide-react"
import { ExceededLimitsMessage } from "@shadcn/components/atoms/messages/ExceededLimitsMessage"
import { Skeleton } from "shared/src/components/shadcn/components/ui/skeleton"

export function TeamTab({ colWidths }) {
	const { defaultUserRole = "recruiter", userRoles, userType, evoConfig } = useConfigContext()
	const { company, setCompany, user } = useAppContext()
	const [roleChangeLoadingFlags, setRoleChangeLoadingFlags] = useState([])
	const [isLoading, setIsLoading] = useState(false)
	const [isLoadingUsers, setIsLoadingUsers] = useState(true)
	const [inviteFields, handleInviteFields, setInviteFields] = useFormFields({
		name: "",
		email: "",
	})
	const availableUsers = company?.limits?.users?.limit ?? DEFAULT_AVAILABLE_USERS

	const [directDbUsers, setDirectDbUsers] = useState<{ [userId: string]: UserFromDb }>({})
	const numOfUsers = Object.keys(directDbUsers).length
	const showUsersLimit = company?.limits?.users?.showToUsers ?? false
	const canAddNewUser = numOfUsers < availableUsers || !company?.limits?.users?.enforced
	const isOverLimit = numOfUsers > availableUsers
	const percentageUsage = Math.round((numOfUsers / availableUsers) * 100)
	const percentageUsageRef = useRef<number | null>(null)

	const { showAlertInfo, showConfirmDelete, showConfirm, DialogComponent } = useConfirmationDialog()

	useMountEffect(() => {
		setIsLoadingUsers(true)
		getUsers({ clientId: company.id })
			.then((users) => {
				setDirectDbUsers(
					users.reduce((indexedUsers, user) => {
						indexedUsers[user.sk.slice(4)] = user
						return indexedUsers
					}, {})
				)
			})
			.finally(() => {
				setIsLoadingUsers(false)
			})
	})

	const users: { [userId: string]: UserFromDb | UserFromClient } = useMemo(
		() => ({ ...company.team, ...directDbUsers }),
		[company.team, directDbUsers]
	)

	useEffect(() => {
		const numberOfTeamMembers = Object.keys(company?.team ?? {}).length
		setRoleChangeLoadingFlags(Array(numberOfTeamMembers).fill(false))
	}, [company.team])

	useEffect(() => {
		if (percentageUsage !== percentageUsageRef.current) {
			window.Intercom?.("update", { company: { id: company.id, "users usage": percentageUsage } })
			percentageUsageRef.current = percentageUsage
		}
	}, [company.id, percentageUsage])

	async function onSendInvite(event) {
		event.preventDefault()
		setIsLoading(true)
		try {
			await createUser({
				userEmail: inviteFields.email,
				userName: inviteFields.name,
				userRole: defaultUserRole,
				companyId: company.id,
				company: company.name,
				userType,
			})

			// Show success toast
			toast.success("User invited successfully", {
				description: `Invitation sent to ${inviteFields.email}`,
			})

			// Clear form fields
			setInviteFields({ name: "", email: "" })

			// Refresh user list from database
			const updatedUsers = await getUsers({ clientId: company.id })
			setDirectDbUsers(
				updatedUsers.reduce((indexedUsers, user) => {
					indexedUsers[user.sk.slice(4)] = user
					return indexedUsers
				}, {})
			)

			setCompany({
				...company, //triggers re-fetch so no need to add new user and risk duplicating
			})
		} catch (e) {
			if (e.message.includes("500")) {
				await showAlertInfo(
					"Unable to add user, this may occur when they already have an account.",
					"Error Adding User"
				)
			} else if (e.message.includes("403")) {
				await showAlertInfo("Failed to add user (Are you an admin?)", "Permission Denied")
			} else if (e.message.includes("400")) {
				await showAlertInfo("Failed to add user", "Error")
			}
		}
		setIsLoading(false)
	}

	function makeOptions(userId, currentRole, idx) {
		const possibleRoles = Array.from(userRoles ?? ["admin", "recruiter", "brand editor"])
		const roleIndex = possibleRoles.indexOf(currentRole)
		if (roleIndex !== -1) {
			possibleRoles.splice(roleIndex, 1)
		}
		return possibleRoles
			.map((role) => ({ text: "Change to " + capitalizeString(role), onClick: makeChangeRoleClick(userId, role, idx) }))
			.concat({ text: "Delete", onClick: makeDeleteClick(userId, idx) })
	}

	function validateInviteForm() {
		return inviteFields.name.length > 0 && validateEmail(inviteFields.email)
	}

	function makeDeleteClick(userId, idx) {
		return async function (event) {
			event.preventDefault()
			//set loading
			roleChangeLoadingFlags[idx] = true
			setRoleChangeLoadingFlags([...roleChangeLoadingFlags])
			//Make change
			await deleteUserWithChecks(userId)
			//set not loading
			roleChangeLoadingFlags[idx] = false
			setRoleChangeLoadingFlags([...roleChangeLoadingFlags])
		}
	}

	async function deleteUserWithChecks(userId) {
		if (user.role === "admin") {
			if (userId === user.id) {
				await showAlertInfo("An admin cannot delete themselves", "Cannot Delete")
				return
			}
			const userEmail = users[userId]?.email
			const userName = users[userId]?.name
			const confirmed = await showConfirmDelete("Are you sure you want to delete this user?", "Delete User", "Delete")
			if (confirmed) {
				await deleteUser({ userId, companyId: company.id, company: company.name, userEmail })

				// Show success toast
				toast.success("User deleted successfully", {
					description: `${userName} has been removed from the team`,
				})

				// Refresh user list from database
				const updatedUsers = await getUsers({ clientId: company.id })
				setDirectDbUsers(
					updatedUsers.reduce((indexedUsers, user) => {
						indexedUsers[user.sk.slice(4)] = user
						return indexedUsers
					}, {})
				)

				delete company.team[userId]
				setCompany({ ...company })
			}
		} else {
			await showAlertInfo("Only an admin may delete a user", "Permission Denied")
		}
	}

	function makeChangeRoleClick(changingUser, newRole, idx) {
		return async function onClick(event) {
			event.preventDefault()
			//set loading
			roleChangeLoadingFlags[idx] = true
			setRoleChangeLoadingFlags([...roleChangeLoadingFlags])
			//Make change
			await changeRole(changingUser, newRole)
			//set not loading
			roleChangeLoadingFlags[idx] = false
			setRoleChangeLoadingFlags([...roleChangeLoadingFlags])
		}
	}

	async function changeRole(changingUser, newRole) {
		if (user.role === "admin") {
			if (changingUser === user.id) {
				await showAlertInfo("An admin cannot remove their own admin privileges", "Cannot Change Role")
				return
			}
			await patchUser({
				userId: changingUser,
				email: users[changingUser]?.email,
				userRole: newRole === "brand editor" ? "brandEditor" : newRole,
				clientId: company.id,
				clientName: company.name,
			})
			// Show success toast
			toast.success("User role updated successfully", {
				description: `${users[changingUser].name} is now a ${capitalizeString(newRole)}`,
			})
			// Refresh user list from database
			const updatedUsers = await getUsers({ clientId: company.id })
			setDirectDbUsers(
				updatedUsers.reduce((indexedUsers, user) => {
					indexedUsers[user.sk.slice(4)] = user
					return indexedUsers
				}, {})
			)

			const newCompany = {
				...company,
				...{
					team: {
						...company.team,
						...{ [changingUser]: { name: users[changingUser].name, user_role: newRole } },
					},
				},
			}
			setCompany(newCompany)
		} else {
			await showAlertInfo("Only an admin may change a user's role", "Permission Denied")
		}
	}

	const handleToggleSSO = async (status: boolean) => {
		if (company.ssoEnabled) {
			await showAlertInfo("SSO is already enabled and cannot be turned off.", "SSO Already Enabled")
			return
		}

		const confirmed = await showConfirm(
			<div>
				<p>Enabling SSO will require all users to login through your identity provider. This action is irreversible.</p>
				<p className="mt-2">Are you sure you want to continue?</p>
			</div>,
			"Enable SSO",
			"Enable SSO"
		)

		if (confirmed) {
			setIsLoading(true)
			clientApi
				.setSSOEnabled({ clientId: company.id, ssoEnabled: true })
				.then(() => {
					company.ssoEnabled = true
					setCompany({ ...company })
				})
				.finally(() => {
					setIsLoading(false)
				})
		}
	}

	return (
		<div
			className="space-y-6"
			style={company.evoEnabled ? { pointerEvents: "none", opacity: 0.7 } : { pointerEvents: "auto" }}>
			{/* Evo Platform Alert */}
			{company.evoEnabled && (
				<Alert variant="info">
					<Info className="h-5 w-5" />
					<div>
						<AlertDescription className="font-semibold">
							This account is managed from the Evo Platform.
						</AlertDescription>
						<AlertDescription className="mt-1">Please manage user access from there.</AlertDescription>
					</div>
				</Alert>
			)}

			{/* Login Method Card */}
			{evoConfig?.IDENTITY_ENABLED && (
				<Card>
					<CardHeader>
						<CardTitle>Login Method</CardTitle>
						<CardDescription>Configure how users authenticate.</CardDescription>
					</CardHeader>
					<CardContent>
						<Field orientation="horizontal" className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-center">
							<FieldLabel htmlFor="sso-toggle">Enable SSO</FieldLabel>
							<FieldContent>
								<div className="flex items-center gap-3">
									<Switch id="sso-toggle" checked={company.ssoEnabled} onCheckedChange={handleToggleSSO} />
									<span className="text-sm text-muted-foreground">
										Require users to login through your identity provider
									</span>
								</div>
							</FieldContent>
						</Field>
					</CardContent>
				</Card>
			)}

			{/* Team Overview Card */}
			<Card>
				<CardHeader>
					<CardTitle>Team Overview</CardTitle>
					<CardDescription>Manage your team members and their permissions.</CardDescription>
				</CardHeader>
				<CardContent>
					<Field orientation="horizontal" className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-center">
						<FieldLabel>Number of users</FieldLabel>
						<FieldContent>
							{isLoadingUsers ? (
								<Skeleton className="h-6 w-24" />
							) : showUsersLimit ? (
								<LimitBadge used={numOfUsers} limit={availableUsers} label="users" />
							) : (
								<span className="text-sm font-medium text-foreground">{numOfUsers} users</span>
							)}
						</FieldContent>
					</Field>
					{showUsersLimit && isOverLimit && <ExceededLimitsMessage limitsOn="users" />}
				</CardContent>
			</Card>

			{/* Invite New User Card */}
			<Card>
				<CardHeader>
					<CardTitle>Invite New User</CardTitle>
					<CardDescription>Send an invitation email to add a new team member.</CardDescription>
				</CardHeader>
				<CardContent>
					<form id="invite-new-user-form" onSubmit={onSendInvite} className="space-y-6 max-w-4xl">
						{/* <div className="grid gap-4 grid-cols-1 sm:grid-cols-2"> */}
						<Field orientation="horizontal" className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start">
							<FieldLabel htmlFor="name">Name</FieldLabel>
							<FieldContent className="max-w-md">
								<Input
									id="name"
									name="name"
									type="text"
									placeholder="Enter name"
									value={inviteFields.name}
									onChange={handleInviteFields}
									autoComplete="name"
									aria-invalid={inviteFields.email?.length > 0 && !inviteFields.name}
									disabled={isLoading}
								/>
							</FieldContent>
						</Field>
						<Field orientation="horizontal" className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start">
							<FieldLabel htmlFor="email">Email</FieldLabel>
							<FieldContent className="max-w-md">
								<Input
									id="email"
									name="email"
									type="email"
									placeholder="Enter email"
									value={inviteFields.email}
									onChange={handleInviteFields}
									autoComplete="email"
									aria-invalid={inviteFields.email?.length > 0 && !validateEmail(inviteFields.email)}
									disabled={isLoading}
								/>
							</FieldContent>
						</Field>
						{/* </div> */}
						<div className="flex flex-col gap-3">
							<Button
								type="submit"
								className="w-fit sm:ml-[168px]"
								disabled={!validateInviteForm() || !canAddNewUser}
								loading={isLoading}>
								Send Invitation
							</Button>
							{!canAddNewUser && (
								<div className="flex items-center gap-2 text-sm text-error-600">
									<CircleAlert className="h-4 w-4" />
									<span>User limit reached. Contact support to upgrade.</span>
								</div>
							)}
						</div>
					</form>
				</CardContent>
			</Card>

			{/* Current Users Card */}
			<Card>
				<CardHeader>
					<CardTitle>Current Users</CardTitle>
					<CardDescription>
						{isLoadingUsers ? <Skeleton className="h-4 w-32" /> : `${numOfUsers} team members`}
					</CardDescription>
				</CardHeader>
				<CardContent className="px-0">
					<Table className="table-fixed">
						<TableHeader>
							<TableRow>
								<TableHead className="px-6 w-1/4">Name</TableHead>
								<TableHead className="px-6 w-1/4">Role</TableHead>
								<TableHead className="px-6 w-1/4">Last Login</TableHead>
								<TableHead className="px-6 w-1/4">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{Object.entries(users).map(([userId, userInfo], idx) => {
								const currentRole = getUserRole(userInfo)
								const possibleRoles = Array.from(userRoles ?? ["admin", "recruiter", "brand editor"])

								return (
									<TableRow key={userId}>
										<TableCell className="px-6 py-3">
											<div className="font-medium text-foreground">{userInfo.name}</div>
										</TableCell>
										<TableCell className="px-6 py-3">
											<Select
												value={currentRole}
												onValueChange={(newRole) => {
													makeChangeRoleClick(userId, newRole, idx)({ preventDefault: () => {} })
												}}
												disabled={roleChangeLoadingFlags[idx]}>
												<SelectTrigger className="w-[180px]">
													<SelectValue>{capitalizeString(currentRole)}</SelectValue>
												</SelectTrigger>
												<SelectContent>
													{possibleRoles.map((role) => (
														<SelectItem key={role} value={role}>
															{capitalizeString(role)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</TableCell>
										<TableCell className="px-6 py-3">
											<span className="text-sm text-muted-foreground">
												{userInfo.lastLogin == null
													? "—"
													: `${getHumanReadableAge(parseInt(userInfo.lastLogin), 1)} ago`}
											</span>
										</TableCell>
										<TableCell className="px-6 py-3">
											<IconButton
												icon={Trash2}
												variant="destructive"
												onClick={() => {
													makeDeleteClick(userId, idx)({ preventDefault: () => {} })
												}}
												loading={roleChangeLoadingFlags[idx]}
											/>
										</TableCell>
									</TableRow>
								)
							})}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			{/* Confirmation Dialog */}
			{DialogComponent}
		</div>
	)
}
